import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Skull, Flame, LogOut, Backpack, Sword, Scroll, Hammer, Map as MapIcon, Globe, LifeBuoy, Shield, MessageSquare, ScrollText, Settings } from "lucide-react";
import TravelPanel from "@/components/TravelPanel";
import CharacterPanel from "@/components/CharacterPanel";
import InventoryPanel from "@/components/InventoryPanel";
import SkillsPanel from "@/components/SkillsPanel";
import QuestsPanel from "@/components/QuestsPanel";
import WorldPanel from "@/components/WorldPanel";
import CombatOverlay from "@/components/CombatOverlay";
import VictorySplash from "@/components/VictorySplash";
import BossKillCinematic from "@/components/BossKillCinematic";
import { WelcomeBackBox } from "@/components/WelcomeBackBox";
import { classChibi } from "@/lib/classChibi";
import VitalsHeader from "@/components/VitalsHeader";
import EventBanner from "@/components/EventBanner";
import Portrait from "@/components/Portrait";
import NotificationBell from "@/components/NotificationBell";
import GlobalChat from "@/components/GlobalChat";
import BloodMoonCountdown from "@/components/BloodMoonCountdown";
import SoundToggle from "@/components/SoundToggle";
import ClassTipsModal from "@/components/ClassTipsModal";
import SupportTickets from "@/components/SupportTickets";
import AdminPanel from "@/components/AdminPanel";
import Onboarding from "@/components/Onboarding";
import SponsorshipBanner from "@/components/SponsorshipBanner";
import SettingsPanel from "@/components/SettingsPanel";
import LanguageSelector from "@/components/LanguageSelector";
import LanguageOnboardingModal from "@/components/LanguageOnboardingModal";
import { applyAccessibility } from "@/lib/accessibility";
import { SFX } from "@/lib/sounds";

const TABS = [
  { id: "travel", labelKey: "nav.travel", icon: MapIcon },
  { id: "character", labelKey: "nav.character", icon: Sword },
  { id: "inventory", labelKey: "nav.inventory", icon: Backpack },
  { id: "skills", labelKey: "nav.skills", icon: Hammer },
  { id: "quests", labelKey: "nav.quests", icon: Scroll },
  { id: "world", labelKey: "nav.world", icon: Globe },
];

export default function Game() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [ch, setCh] = useState(null);

  // Prefetch: al primo mount del Game, riscalda la cache di traduzione con
  // ~40 stringhe comuni della UI così il primo render della lingua target
  // trova tutto già tradotto (nessun flash di inglese/italiano).
  useEffect(() => {
    const lang = (i18n.language || "").toLowerCase();
    if (!lang || lang === "it") return;
    const COMMON_STRINGS = [
      "Loading", "Save", "Cancel", "Close", "Edit", "Delete", "Confirm", "Attack", "Flee",
      "Defend", "Magic", "Special", "Item", "Consumables", "Use", "Equip", "Unequip", "Sell",
      "Dismantle", "Class Sets", "Forge", "Your Referral", "Redeem a Code", "Season Pass",
      "Founding Guild", "Found a Guild", "Post Recruitment Banner", "Weekly World Boss",
      "Contributors", "Biology Bench", "World Chronicle", "The world holds its breath.",
      "Your Party", "Forging Card…", "Share Trophy Card", "Sponsor / Collab", "Scrivici",
      "Vuoi entrare nell'ombra?", "Notifications", "Bestiary", "Codex",
    ];
    api.translateTexts(COMMON_STRINGS, lang).catch(() => {});
    // Also warm up the last 20 adventure_log entries so TravelPanel opens
    // the Cronaca already translated (no flash of Italian text).
    if (ch?.adventure_log?.length) {
      const logTexts = Array.from(new Set(
        ch.adventure_log.slice(-20).map(e => e?.text).filter(Boolean)
      ));
      if (logTexts.length) api.translateTexts(logTexts, lang).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);
  const [tab, setTab] = useState("travel");
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [mod, setMod] = useState(null);
  const [supportOpen, setSupportOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Show the first-run language picker until the player either taps a flag
  // or has already set `preferences.language` on the server. Once dismissed
  // we set `gw_language_chosen=1` in localStorage so the modal never
  // reappears on this device even if the server preference is later cleared.
  const [langOnboardOpen, setLangOnboardOpen] = useState(false);
  // Post-combat celebration overlay. Lives here (not inside CombatOverlay)
  // so it persists after `ch.combat` is cleared and the combat overlay unmounts.
  const [pendingVictory, setPendingVictory] = useState(null);
  // World Boss kill cinematic report — set when the killing blow lands.
  const [bossKillReport, setBossKillReport] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const data = await api.character();
      setCh(data);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/login");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 20000); // polling for regen/job idle
    return () => clearInterval(t);
  }, [refresh]);

  // Sync the server-saved language preference onto i18next once the character
  // loads. localStorage still wins on first render for a snappy UX; the
  // server value overrides it only if it differs and the user hasn't changed
  // languages this session.
  useEffect(() => {
    if (!ch) return;
    const lang = ch.preferences?.language;
    if (lang && lang !== i18n.language) {
      try { localStorage.setItem("gw_language", lang); } catch (e) { /* noop */ }
      i18n.changeLanguage(lang);
    }
    // First-run picker: show unless the server already knows the choice OR
    // the player has explicitly dismissed the modal on this device.
    let dismissed = false;
    try { dismissed = localStorage.getItem("gw_language_chosen") === "1"; } catch (e) { /* noop */ }
    if (!lang && !dismissed) {
      setLangOnboardOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ch?.preferences?.language, ch?.user_id]);

  // Apply accessibility preferences to <html> whenever the character updates.
  useEffect(() => {
    applyAccessibility(ch?.preferences);
  }, [ch?.preferences]);

  useEffect(() => {
    const pull = () => api.eventCurrent().then(setEvent).catch(() => {});
    pull();
    const t = setInterval(pull, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const pull = () => api.moderationMe().then(setMod).catch(() => {});
    pull();
    const t = setInterval(pull, 30000);
    return () => clearInterval(t);
  }, []);

  const bloodMoon = event?.active && event?.id === "blood_moon";

  const logout = async () => {
    await api.logout().catch(() => {});
    navigate("/login");
  };

  if (loading || !ch) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#8c8c8c] font-heading tracking-widest">
        <span className="ember-pulse">AWAKENING...</span>
      </div>
    );
  }

  // First-time onboarding: mandatory name + class before any gameplay.
  if (!ch.onboarded) {
    return <Onboarding onDone={setCh} />;
  }

  const TAB_BG = {
    travel:    "radial-gradient(ellipse at 20% 10%, rgba(138,3,3,0.10), transparent 60%), radial-gradient(ellipse at 80% 90%, rgba(229,90,0,0.08), transparent 60%), linear-gradient(180deg, #060606, #050505)",
    character: "radial-gradient(ellipse at 50% 15%, rgba(184,156,58,0.08), transparent 55%), linear-gradient(180deg, #070707, #050505)",
    inventory: "radial-gradient(ellipse at 30% 30%, rgba(74,123,199,0.06), transparent 60%), linear-gradient(180deg, #060606, #050505)",
    skills:    "radial-gradient(ellipse at 70% 20%, rgba(110,139,61,0.08), transparent 60%), linear-gradient(180deg, #060707, #050505)",
    quests:    "radial-gradient(ellipse at 40% 20%, rgba(180,75,224,0.06), transparent 60%), linear-gradient(180deg, #060606, #050505)",
    world:     "radial-gradient(ellipse at 20% 20%, rgba(90,10,10,0.12), transparent 60%), radial-gradient(ellipse at 80% 80%, rgba(58,31,94,0.10), transparent 60%), linear-gradient(180deg, #060606, #030303)",
  };

  return (
    <div
      className="min-h-screen w-full mx-auto pb-20 relative"
      style={{
        maxWidth: "min(1024px, 100%)",
        background: TAB_BG[tab] || TAB_BG.travel,
        transition: "background 0.5s ease",
      }}
      data-testid="game-root"
      data-tab={tab}
    >
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[#050505] border-b border-[#262626]">
        <EventBanner />
        <BloodMoonCountdown />
        <div className="flex items-center gap-2 px-3 py-2">
          <Portrait
            name={ch.name}
            cls={ch.class}
            cloak_color={ch.cosmetics_equipped?.cloak ? undefined : undefined}
            size={32}
            active_title={ch.active_title}
            appearance={ch.appearance}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-heading tracking-[0.2em] text-xs text-[#E2DFD8] truncate" data-testid="hero-name">
                {ch.name}
              </span>
              <span className="font-mono text-[10px] text-[#8c8c8c] uppercase" data-testid="hero-class">
                {ch.class} · Lv {ch.level}
              </span>
            </div>
            {ch.active_title && (
              <div
                className="font-heading text-[10px] tracking-widest uppercase text-[#B89C3A]"
                data-testid="hero-title"
              >
                « {ch.active_title.replace(/_/g, " ")} »
              </div>
            )}
          </div>
          <button
            data-testid="support-open"
            onClick={() => setSupportOpen(true)}
            className={`shrink-0 ${mod?.muted ? "text-[#E33333] ember-pulse" : "text-[#8c8c8c] hover:text-[#B89C3A]"}`}
            aria-label="Support"
            title={mod?.muted ? "You are muted — appeal here" : "Support"}
          >
            <LifeBuoy className="w-4 h-4" strokeWidth={1.5} />
          </button>
          {mod?.is_admin && (
            <button
              data-testid="admin-open"
              onClick={() => setAdminOpen(true)}
              className="shrink-0 text-[#E5C34A] hover:text-[#E55A00]"
              aria-label="Warden Console"
              title="Warden Console"
            >
              <Shield className="w-4 h-4" strokeWidth={1.5} />
            </button>
          )}
          {mod?.is_admin && (
            <a
              data-testid="admin-discord-btn"
              href="/admin/discord"
              className="shrink-0 text-[#5865F2] hover:text-[#8FA5FF]"
              aria-label="Gestione Discord"
              title="Gestione Discord"
            >
              <MessageSquare className="w-4 h-4" strokeWidth={1.5} />
            </a>
          )}
          {mod?.is_admin && (
            <a
              data-testid="admin-gm-btn"
              href="/admin/gm"
              className="shrink-0 text-[#A97AB0] hover:text-[#C9A0DC]"
              aria-label="Game Master Console"
              title="Game Master · gestione nemici, oggetti, classi, personaggi"
            >
              <ScrollText className="w-4 h-4" strokeWidth={1.5} />
            </a>
          )}
          <button
            data-testid="settings-open"
            onClick={() => setSettingsOpen(true)}
            className="shrink-0 text-[#8c8c8c] hover:text-[#E5C34A]"
            aria-label="Impostazioni"
            title="Impostazioni"
          >
            <Settings className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <button
            data-testid="logout-button"
            onClick={logout}
            className="text-[#8c8c8c] hover:text-[#E55A00] shrink-0"
            aria-label="Logout"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <SoundToggle />
          <NotificationBell />
          <LanguageSelector variant="compact" />
        </div>
        <VitalsHeader ch={ch} />
      </header>

      <main className="px-3 py-4">
        {tab === "travel" && <TravelPanel ch={ch} setCh={setCh} />}
        {tab === "character" && <CharacterPanel ch={ch} setCh={setCh} />}
        {tab === "inventory" && <InventoryPanel ch={ch} setCh={setCh} />}
        {tab === "skills" && <SkillsPanel ch={ch} setCh={setCh} />}
        {tab === "quests" && <QuestsPanel ch={ch} setCh={setCh} />}
        {tab === "world" && <WorldPanel ch={ch} setCh={setCh} onBossKilled={setBossKillReport} />}
      </main>

      {/* Bottom nav */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-40 bg-[#050505] border-t-2 border-[#262626]"
        data-testid="bottom-nav"
      >
        <div className="mx-auto grid grid-cols-6" style={{ maxWidth: "min(1024px, 100%)" }}>
          {TABS.map(item => {
            const Icon = item.icon;
            const active = tab === item.id;
            const shouldPulse = item.id === "world" && bloodMoon && tab !== "world";
            return (
              <button
                key={item.id}
                data-testid={`nav-${item.id}`}
                data-blood-moon={shouldPulse ? "1" : "0"}
                onClick={() => setTab(item.id)}
                className={`relative flex flex-col items-center gap-1 py-3 border-t-2 transition-colors ${
                  active ? "border-[#E55A00] text-[#E55A00]" : "border-transparent text-[#8c8c8c] hover:text-[#E2DFD8]"
                } ${shouldPulse ? "nav-pulse-blood" : ""}`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.5} />
                <span className="font-heading text-[10px] tracking-[0.2em] uppercase">{t(item.labelKey)}</span>
                {shouldPulse && (
                  <span
                    className="absolute top-1 right-1 w-2 h-2 rounded-full"
                    style={{ background: "#B02020", boxShadow: "0 0 8px #B02020" }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {ch.combat && <CombatOverlay ch={ch} setCh={setCh} onVictory={setPendingVictory} />}
      {pendingVictory && (
        <VictorySplash
          chibi={classChibi(ch.chosen_class || ch.class, ch.appearance?.gender, ch.appearance?.class_image)}
          cls={ch.chosen_class || ch.class || "Warrior"}
          name={ch.name}
          victory={pendingVictory}
          onDone={() => setPendingVictory(null)}
        />
      )}
      {bossKillReport && (
        <BossKillCinematic
          report={bossKillReport}
          onDone={() => setBossKillReport(null)}
        />
      )}
      <GlobalChat />
      <WelcomeBackBox ch={ch} setCh={setCh} />
      {supportOpen && <SupportTickets onClose={() => setSupportOpen(false)} />}
      {adminOpen && mod?.is_admin && <AdminPanel onClose={() => setAdminOpen(false)} />}
      {settingsOpen && (
        <SettingsPanel
          ch={ch}
          onClose={() => setSettingsOpen(false)}
          onUpdated={(patch) => setCh(prev => prev ? { ...prev, ...patch } : prev)}
        />
      )}
      <SponsorshipBanner />
      {langOnboardOpen && (
        <LanguageOnboardingModal
          onDismiss={() => {
            setLangOnboardOpen(false);
            // Optimistic sync so a follow-up refresh already sees the choice.
            setCh(prev => prev ? {
              ...prev,
              preferences: {
                ...(prev.preferences || {}),
                language: (typeof window !== "undefined" && window.localStorage
                  ? window.localStorage.getItem("gw_language")
                  : null) || prev.preferences?.language,
              }
            } : prev);
          }}
        />
      )}
    </div>
  );
}
